import { useEffect, useState } from 'react';
import SelectField from '../common/SelectField.js';
import vehicleOwnerService from '../../services/VehicleOwnerService.js';
import { inputFilters } from '../common/Validation.js';
import { mergeCanAccess } from '../../utils/companyFeatures.js';
import { useAuth } from '../../context/AuthContext';
import { extractVehicleOwnersList } from '../../utils/vehicleOwnerDisplay.js';
import { useTranslation } from 'react-i18next';


const EMPTY_NEW_OWNER = {
  owner_name: '',
  contact: '',
  email: '',
  owner_percentage: '',
};

export default function VehicleOwnerAssignmentFields({
  ownerMode,
  setOwnerMode,
  selectedOwnerId,
  setSelectedOwnerId,
  newOwnerData,
  setNewOwnerData,
  ownerPercentageOverride,
  setOwnerPercentageOverride,
  initialOwnerId = '',
}) {
  const { user } = useAuth();
  const companyAccess = mergeCanAccess(user?.company_features || user?.company_can_access || {});
  const hasCommission = Boolean(companyAccess.commission);

  const [ownersLoading, setOwnersLoading] = useState(true);
  const [vehicleOwners, setVehicleOwners] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setOwnersLoading(true);
        const res = await vehicleOwnerService.getAll(1, 500);
        const list = extractVehicleOwnersList(res).filter((o) => o?.vehicle_owner_id);
        if (!cancelled) setVehicleOwners(list);
      } catch (err) {
        console.error('Failed to load vehicle owners:', err);
        if (!cancelled) setVehicleOwners([]);
      } finally {
        if (!cancelled) setOwnersLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (initialOwnerId && !selectedOwnerId) {
      setOwnerMode('existing');
      setSelectedOwnerId(initialOwnerId);
    }
  }, [initialOwnerId, selectedOwnerId, setOwnerMode, setSelectedOwnerId]);

  const selectedOwner = vehicleOwners.find((o) => o.vehicle_owner_id === selectedOwnerId);

  const needsOwnerPercentage =
    hasCommission &&
    ownerMode === 'existing' &&
    selectedOwner &&
    (selectedOwner.owner_percentage === null || selectedOwner.owner_percentage === undefined);

  const handleNewOwnerChange = (e) => {
    const { name, value } = e.target;
    let filtered = value;
    if (name === 'contact') filtered = inputFilters.digits10(value);
    else if (name === 'owner_percentage') filtered = inputFilters.amount(value);
    setNewOwnerData((prev) => ({ ...prev, [name]: filtered }));
  };

  return (
    <div className="mt-6 pt-6 border-t border-gray-200">
      <h4 className="text-base font-semibold text-gray-700 mb-1">Vehicle Owner</h4>
      <p className="text-xs text-gray-500 mb-4">
        Optional. Assign this vehicle to an owner for shipment commission tracking (same as ML sheet create).
      </p>

      <div className="flex flex-wrap gap-4 mb-4">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="radio"
            name="vehicleOwnerMode"
            checked={ownerMode === 'none'}
            onChange={() => setOwnerMode('none')}
          />
          No owner
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="radio"
            name="vehicleOwnerMode"
            checked={ownerMode === 'existing'}
            onChange={() => setOwnerMode('existing')}
          />
          Existing owner
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="radio"
            name="vehicleOwnerMode"
            checked={ownerMode === 'new'}
            onChange={() => setOwnerMode('new')}
          />
          New owner
        </label>
      </div>

      {ownerMode === 'existing' && (
        <div className="grid grid-cols-2 sm:grid-cols-2 gap-2 sm:gap-4">
          <div>
            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
              Select Owner
            </label>
            <SelectField variant="form"
              value={selectedOwnerId}
              onChange={(e) => {
                setSelectedOwnerId(e.target.value);
                setOwnerPercentageOverride('');
              }}
              disabled={ownersLoading}
              className="w-full px-3 sm:px-4 py-2 border border-gray-300 text-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base bg-white"
            >
              <option value="" disabled hidden>
                {ownersLoading ? 'Loading owners...' : 'Select vehicle owner'}
              </option>
              {!ownersLoading &&
                vehicleOwners.map((owner) => (
                  <option
                    key={owner.vehicle_owner_id}
                    value={owner.vehicle_owner_id}
                    className="text-gray-700"
                  >
                    {owner.owner_name}
                    {owner.contact ? ` (${owner.contact})` : ''}
                  </option>
                ))}
            </SelectField>
          </div>
          {needsOwnerPercentage && (
            <div>
              <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                Owner Percentage (%) <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={ownerPercentageOverride}
                onChange={(e) => setOwnerPercentageOverride(inputFilters.amount(e.target.value))}
                placeholder="0–100"
                className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">Optional — owner has no % set yet.</p>
            </div>
          )}
        </div>
      )}

      {ownerMode === 'new' && (
        <div className="grid grid-cols-2 sm:grid-cols-2 xl:grid-cols-4 gap-2 sm:gap-4">
          <div>
            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
              Owner Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="owner_name"
              value={newOwnerData.owner_name}
              onChange={handleNewOwnerChange}
              className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
              Contact <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="contact"
              value={newOwnerData.contact}
              onChange={handleNewOwnerChange}
              maxLength={10}
              className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
              Email (Optional)
            </label>
            <input
              type="email"
              name="email"
              value={newOwnerData.email}
              onChange={handleNewOwnerChange}
              className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
            />
          </div>
          {hasCommission && (
            <div>
              <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                Owner Percentage (%) <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                type="text"
                name="owner_percentage"
                value={newOwnerData.owner_percentage}
                onChange={handleNewOwnerChange}
                placeholder="0–100"
                className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export { EMPTY_NEW_OWNER };
